import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';

export interface OpenTab {
  id: string;
  title: string;
  dirty: boolean;
}

/** Callback den eine ReportDetailPage registriert um Close-Anfragen zu behandeln. */
type CloseHandler = () => boolean; // returns true = darf schliessen, false = blockiert (zeigt Dialog)

interface TabContextValue {
  tabs: OpenTab[];
  activeTabId: string | null;
  openTab: (id: string, title?: string) => void;
  closeTab: (id: string) => void;
  /** Schliesst Tab sofort ohne Rueckfrage (nach Dialog-Bestaetigung). */
  forceCloseTab: (id: string) => void;
  setTabTitle: (id: string, title: string) => void;
  setTabDirty: (id: string, dirty: boolean) => void;
  switchTab: (id: string) => void;
  /** ReportDetailPage registriert einen Handler der bei Tab-X aufgerufen wird. */
  registerCloseHandler: (id: string, handler: CloseHandler) => void;
  unregisterCloseHandler: (id: string) => void;
}

const TabContext = createContext<TabContextValue>({
  tabs: [],
  activeTabId: null,
  openTab: () => {},
  closeTab: () => {},
  forceCloseTab: () => {},
  setTabTitle: () => {},
  setTabDirty: () => {},
  switchTab: () => {},
  registerCloseHandler: () => {},
  unregisterCloseHandler: () => {},
});

export function useReportTabs() {
  return useContext(TabContext);
}

export function TabProvider({ children, onNavigate }: { children: ReactNode; onNavigate: (path: string) => void }) {
  const [tabs, setTabs] = useState<OpenTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const closeHandlersRef = useRef<Map<string, CloseHandler>>(new Map());

  const openTab = useCallback((id: string, title?: string) => {
    setTabs((prev) => {
      if (prev.some((t) => t.id === id)) return prev;
      return [...prev, { id, title: title || 'Bericht...', dirty: false }];
    });
    setActiveTabId(id);
  }, []);

  const removeTab = useCallback((id: string) => {
    closeHandlersRef.current.delete(id);
    setTabs((prev) => prev.filter((t) => t.id !== id));
    setActiveTabId((prev) => {
      if (prev !== id) return prev;
      return null;
    });
    onNavigate('/m/bauberichte/reports');
  }, [onNavigate]);

  const closeTab = useCallback((id: string) => {
    const handler = closeHandlersRef.current.get(id);
    if (handler) {
      const canClose = handler();
      if (!canClose) return; // Dialog wird angezeigt
    }
    removeTab(id);
  }, [removeTab]);

  const forceCloseTab = useCallback((id: string) => {
    removeTab(id);
  }, [removeTab]);

  const setTabTitle = useCallback((id: string, title: string) => {
    setTabs((prev) => prev.map((t) => t.id === id ? { ...t, title } : t));
  }, []);

  const setTabDirty = useCallback((id: string, dirty: boolean) => {
    setTabs((prev) => prev.map((t) => t.id === id ? { ...t, dirty } : t));
  }, []);

  const switchTab = useCallback((id: string) => {
    setActiveTabId(id);
    onNavigate(`/m/bauberichte/reports/${id}`);
  }, [onNavigate]);

  const registerCloseHandler = useCallback((id: string, handler: CloseHandler) => {
    closeHandlersRef.current.set(id, handler);
  }, []);

  const unregisterCloseHandler = useCallback((id: string) => {
    closeHandlersRef.current.delete(id);
  }, []);

  return (
    <TabContext.Provider value={{
      tabs, activeTabId, openTab, closeTab, forceCloseTab,
      setTabTitle, setTabDirty, switchTab,
      registerCloseHandler, unregisterCloseHandler,
    }}>
      {children}
    </TabContext.Provider>
  );
}
